import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { ArrowRight, ArrowLeft, User, Phone, Mail, Lock, Users as UsersIcon, Check, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import InstallAppPrompt from '../components/shared/InstallAppPrompt';
import { trackInstallEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const formatPhone = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export default function JoinTeamPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore(s => s.setAuth);

  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', confirm: '' });
  const [selectedKidIds, setSelectedKidIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!teamId) return;
    trackInstallEvent('page_view', { page: 'join', teamId });
    axios.get(`${API}/api/teams/public/${teamId}/roster`)
      .then(r => setTeamData(r.data))
      .catch(() => setError('We couldn\'t find this team. Double-check your link.'))
      .finally(() => setLoading(false));
  }, [teamId]);

  const toggleKid = (pid) => {
    setSelectedKidIds(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };

  const handleJoin = async () => {
    if (!form.name.trim()) return toast.error('Please enter your name');
    if (!form.password || form.password.length < 4) return toast.error('Please set a password (4+ chars)');
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return toast.error('Invalid email');
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits && phoneDigits.length !== 10) return toast.error('Phone must be 10 digits');

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/teams/${teamId}/join`, {
        name: form.name.trim(),
        phone_number: phoneDigits,
        email: form.email.trim().toLowerCase() || null,
        password: form.password,
        kid_player_ids: selectedKidIds,
      });
      setAuth({ ...r.data.user, registeredAs: 'parent' }, r.data.token);
      toast.success(`Welcome! You're following ${r.data.team_name}`);
      navigate('/parent-browse');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join team');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-stone-900 flex items-center justify-center text-white/80">
        <Loader2 size={24} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !teamData) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center p-6 text-center">
        <div className="text-stone-300 max-w-sm">
          <p className="text-xl font-bold mb-2">We hit a snag</p>
          <p className="text-stone-400 text-sm mb-4">{error || 'Team not found'}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-stone-600 text-stone-300">Go home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-stone-900 px-4 py-8" data-testid="join-team-page">
      <div className="max-w-md mx-auto space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/')}
          className="-ml-2 text-green-200 hover:text-white hover:bg-white/10"
          data-testid="join-back-btn"
        >
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <div className="text-center">
          <p className="text-amber-300 text-xs uppercase tracking-widest font-bold mb-1">You&apos;re invited to follow</p>
          <h1
            className="text-3xl md:text-4xl font-black text-white tracking-tight leading-none"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '-0.02em' }}
          >
            {teamData.team_name}
          </h1>
          <p className="text-green-200 text-sm mt-2">
            Get live scores, push alerts, spray charts, and messages from the coach.
          </p>
        </div>

        {/* Install prompt — grandparents need this most */}
        <InstallAppPrompt variant="banner" />

        <Card className="border-0 shadow-xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Create your parent account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="join-name">Your name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                <Input
                  id="join-name"
                  placeholder="Jane Smith"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="pl-10"
                  data-testid="join-name-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="join-phone">Phone <span className="text-xs text-stone-400 font-normal">(helps the coach reach you)</span></Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                <Input
                  id="join-phone"
                  placeholder="(555) 555-5555"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: formatPhone(e.target.value) })}
                  className="pl-10"
                  maxLength={14}
                  data-testid="join-phone-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="join-email">Email <span className="text-xs text-stone-400 font-normal">(for password recovery)</span></Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                <Input
                  id="join-email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  className="pl-10"
                  data-testid="join-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="join-pw">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                  <Input id="join-pw" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="pl-10" data-testid="join-pw-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="join-pw2">Confirm</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                  <Input id="join-pw2" type="password" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} className="pl-10" data-testid="join-pw-confirm" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UsersIcon size={16} className="text-green-700" /> Which kid(s) are yours?
            </CardTitle>
          </CardHeader>
          <CardContent>
            {teamData.roster.length === 0 ? (
              <p className="text-stone-500 text-sm text-center py-4">Coach hasn&apos;t added any players yet — you can come back later.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {teamData.roster.map(p => {
                  const on = selectedKidIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleKid(p.id)}
                      className={cn(
                        "flex items-center gap-2 border rounded-lg px-3 py-2 text-left transition-colors",
                        on ? "bg-green-50 border-green-500 text-green-900" : "bg-white border-stone-200 text-stone-700 hover:border-green-400"
                      )}
                      data-testid={`join-kid-${p.id}`}
                    >
                      {p.jersey_number && (
                        <span className={cn("text-xs font-bold w-5", on ? "text-green-700" : "text-stone-400")}>#{p.jersey_number}</span>
                      )}
                      <span className="text-sm font-semibold flex-1 truncate">{p.name}</span>
                      {on && <Check size={14} className="text-green-600 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-stone-400 mt-2">Pick as many as apply — coaches can skip this if you&apos;re a grandparent, friend, etc.</p>
          </CardContent>
        </Card>

        <Button
          onClick={handleJoin}
          disabled={submitting}
          className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold text-base shadow-lg"
          data-testid="join-submit-btn"
        >
          {submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> Joining…</> : <>Join {teamData.team_name} <ArrowRight size={16} className="ml-2" /></>}
        </Button>

        <div className="text-center text-[11px] text-white/50">
          Already have an account?{' '}
          <button onClick={() => navigate('/login')} className="underline hover:text-white" data-testid="join-switch-login">Log in instead</button>
        </div>
      </div>
    </div>
  );
}
